import { merchantApi } from '@/config/axios'

const BASE = '/merchant-api/hotel/operation'

export interface HotelShopOption {
  id: string | number
  name: string
  status: number
}

export interface HotelRoomTypeOption {
  id: string | number
  name: string
  status: number
}

export interface HotelRoomTypeSku {
  id?: string | number
  ratePlanName: string
  price: number
  marketPrice?: number
  costPrice?: number
  picUrl: string
  stock: number
  breakfastCount: number
  cancellationPolicy: string
  minStay: number
  maxStay: number
  maxGuests: number
  saleStatus: number
}

export interface HotelRoomType extends HotelRoomTypeOption {
  keyword: string
  introduction: string
  description: string
  picUrl: string
  sliderPicUrls: string[]
  sort: number
  bedType: string
  maxGuests: number
  roomSize: number
  facilityCodes: string[]
  auditStatus: number
  auditRemark?: string
  skus: HotelRoomTypeSku[]
}

export interface HotelFacilityOption {
  id: string | number
  code: string
  name: string
  category: string
  icon?: string
}

export interface HotelRoomTypeImportResult {
  createdCount: number
  updatedCount: number
}

export const getAccessibleShops = () =>
  merchantApi.get<HotelShopOption[]>({ url: '/merchant-api/hotel/context/accessible-shops' })

export const getRoomTypeOptions = (shopId?: string | number) =>
  merchantApi.get<HotelRoomTypeOption[]>({ url: `${BASE}/room-type-options`, params: { shopId } })

export const getRoomTypes = (shopId?: string | number) =>
  merchantApi.get<HotelRoomType[]>({ url: `${BASE}/room-type/list`, params: { shopId } })
export const getFacilities = (shopId?: string | number) =>
  merchantApi.get<HotelFacilityOption[]>({ url: `${BASE}/facility/list`, params: { shopId } })
export const createRoomType = (shopId: string | number | undefined, data: any) =>
  merchantApi.post({ url: `${BASE}/room-type/create`, params: { shopId }, data })
export const updateRoomType = (shopId: string | number | undefined, data: any) =>
  merchantApi.put({ url: `${BASE}/room-type/update`, params: { shopId }, data })
export const updateRoomTypeSaleStatus = (
  shopId: string | number | undefined,
  id: string | number,
  onSale: boolean
) => merchantApi.put({ url: `${BASE}/room-type/sale-status`, params: { shopId, id, onSale } })
export const exportRoomTypes = (shopId?: string | number) =>
  merchantApi.download({ url: `${BASE}/room-type/export`, params: { shopId } })
export const getRoomTypeImportTemplate = () =>
  merchantApi.download({ url: `${BASE}/room-type/import-template` })
export const importRoomTypes = (shopId: string | number | undefined, file: File) => {
  const data = new FormData()
  data.append('file', file)
  return merchantApi.upload<HotelRoomTypeImportResult>({
    url: `${BASE}/room-type/import`,
    params: { shopId },
    data
  })
}

export const getRoomPage = (params: any) => merchantApi.get({ url: `${BASE}/room/page`, params })
export const getRoom = (id: string | number, shopId?: string | number) =>
  merchantApi.get({ url: `${BASE}/room/get`, params: { id, shopId } })
export const createRoom = (data: any) => merchantApi.post({ url: `${BASE}/room/create`, data })
export const updateRoom = (data: any) => merchantApi.put({ url: `${BASE}/room/update`, data })
export const deleteRoom = (id: string | number, shopId?: string | number) =>
  merchantApi.delete({ url: `${BASE}/room/delete`, params: { id, shopId } })

export const getPriceCalendar = (params: any) =>
  merchantApi.get({ url: `${BASE}/price-inventory/calendar`, params })
export const batchSetPriceInventory = (shopId: string | number, data: any[]) =>
  merchantApi.post({ url: `${BASE}/price-inventory/batch-set`, params: { shopId }, data })

export const getBookingOrderPage = (params: any) =>
  merchantApi.get({ url: `${BASE}/booking-order/page`, params })
export const getBookingOrderDetail = (id: string | number, shopId?: string | number) =>
  merchantApi.get({ url: `${BASE}/booking-order/detail-with-guests`, params: { id, shopId } })
export const quickCheckIn = (data: any) =>
  merchantApi.post({ url: `${BASE}/booking-order/quick-check-in`, data })
export const assignBookingRoom = (data: any) =>
  merchantApi.put({ url: `${BASE}/booking-order/assign-room`, data })
export const createOrderGuest = (shopId: string | number | undefined, data: any) =>
  merchantApi.post({ url: `${BASE}/booking-order/guest/create`, params: { shopId }, data })
export const updateOrderGuest = (shopId: string | number | undefined, data: any) =>
  merchantApi.put({ url: `${BASE}/booking-order/guest/update`, params: { shopId }, data })
export const deleteOrderGuest = (shopId: string | number | undefined, id: string | number) =>
  merchantApi.delete({ url: `${BASE}/booking-order/guest/delete`, params: { shopId, id } })
export const verifyGuestIdentity = (data: any) =>
  merchantApi.put({ url: `${BASE}/booking-order/guest/identity-verify`, data })
export const viewGuestIdNumber = (shopId: string | number | undefined, guestId: string | number) =>
  merchantApi.get<string>({
    url: `${BASE}/booking-order/guest/id-number`,
    params: { shopId, guestId }
  })
export const collectOrderDeposit = (
  shopId: string | number | undefined,
  orderId: string | number,
  amount: number
) =>
  merchantApi.put({
    url: `${BASE}/booking-order/deposit/collect`,
    params: { shopId, orderId, amount }
  })
export const refundOrderDeposit = (
  shopId: string | number | undefined,
  orderId: string | number,
  reason: string
) =>
  merchantApi.put({
    url: `${BASE}/booking-order/deposit/refund`,
    params: { shopId, orderId, reason }
  })
export const quoteOrderExtension = (data: any) =>
  merchantApi.post({ url: `${BASE}/booking-order/extension/quote`, data })
export const createAndPayOrderExtension = (data: any) =>
  merchantApi.post({ url: `${BASE}/booking-order/extension/create-and-pay`, data })
export const operateBookingOrder = (
  id: string | number,
  action: 'confirm' | 'cancel' | 'check-in' | 'check-out',
  shopId?: string | number,
  reason?: string
) =>
  merchantApi.put({ url: `${BASE}/booking-order/action`, params: { id, action, shopId, reason } })

export const getCleaningPage = (params: any) =>
  merchantApi.get({ url: `${BASE}/cleaning/page`, params })
export const operateCleaning = (params: any) =>
  merchantApi.put({ url: `${BASE}/cleaning/action`, params })
export const getServiceEmployeeOptions = (shopId?: string | number) =>
  merchantApi.get({ url: `${BASE}/service/employee-options`, params: { shopId } })

export const getMaintenancePage = (params: any) =>
  merchantApi.get({ url: `${BASE}/maintenance/page`, params })
export const createMaintenance = (data: any) =>
  merchantApi.post({ url: `${BASE}/maintenance/create`, data })
export const operateMaintenance = (params: any) =>
  merchantApi.put({ url: `${BASE}/maintenance/action`, params })
export const deleteMaintenance = (id: string | number, shopId?: string | number) =>
  merchantApi.delete({ url: `${BASE}/maintenance/delete`, params: { id, shopId } })

export const getShopPolicy = (shopId?: string | number) =>
  merchantApi.get({ url: `${BASE}/shop-policy/get`, params: { shopId } })
export const updateShopPolicy = (data: any) =>
  merchantApi.put({ url: `${BASE}/shop-policy/update`, data })
export const submitProfileChange = (data: any) =>
  merchantApi.post({ url: '/merchant-api/hotel/profile-change/submit', data })
export const getProfileChangePage = (params: any) =>
  merchantApi.get({ url: '/merchant-api/hotel/profile-change/page', params })

export const getStatisticsOverview = (params: any) =>
  merchantApi.get({ url: `${BASE}/statistics/overview`, params })
export const getStatisticsTrend = (params: any) =>
  merchantApi.get({ url: `${BASE}/statistics/trend`, params })

export const getFinanceSummary = (params: any) =>
  merchantApi.get({ url: `${BASE}/finance/summary`, params })
export const getSettlementDetailPage = (params: any) =>
  merchantApi.get({ url: `${BASE}/finance/settlement/page`, params })
export const getSettlementBatchPage = (params: any) =>
  merchantApi.get({ url: `${BASE}/finance/batch/page`, params })
export const getMerchantBillPage = (params: any) =>
  merchantApi.get({ url: `${BASE}/finance/bill/page`, params })
export const getInvoicePage = (params: any) =>
  merchantApi.get({ url: '/merchant-api/hotel/invoice/page', params })
export const issueInvoice = (data: any) =>
  merchantApi.put({ url: '/merchant-api/hotel/invoice/issue', data })
export const rejectInvoice = (data: any) =>
  merchantApi.put({ url: '/merchant-api/hotel/invoice/reject', data })
export const completeInvoiceRed = (data: any) =>
  merchantApi.put({ url: '/merchant-api/hotel/invoice/complete-red', data })

const MANAGEMENT = '/merchant-api/hotel/management'
export const getManagementSettings = (shopId?: string | number) =>
  merchantApi.get({ url: `${MANAGEMENT}/settings`, params: { shopId } })
export const updateManagementSettings = (shopId: string | number | undefined, data: any) =>
  merchantApi.put({ url: `${MANAGEMENT}/settings`, params: { shopId }, data })
export const getHotelChannels = (shopId?: string | number) =>
  merchantApi.get({ url: `${MANAGEMENT}/channel`, params: { shopId } })
export const bindHotelChannel = (shopId: string | number | undefined, data: any) =>
  merchantApi.put({ url: `${MANAGEMENT}/channel`, params: { shopId }, data })
export const unbindHotelChannel = (shopId: string | number | undefined, channelType: string) =>
  merchantApi.delete({ url: `${MANAGEMENT}/channel`, params: { shopId, channelType } })
export const getHotelContentVersions = (shopId: string | number | undefined, contentType: string) =>
  merchantApi.get({ url: `${MANAGEMENT}/content`, params: { shopId, contentType } })
export const saveHotelContentDraft = (shopId: string | number | undefined, data: any) =>
  merchantApi.post({ url: `${MANAGEMENT}/content/draft`, params: { shopId }, data })
export const submitHotelContent = (id: string | number, data: any) =>
  merchantApi.post({ url: `${MANAGEMENT}/content/${id}/submit`, data })
export const getHotelRentPolicies = (shopId?: string | number) =>
  merchantApi.get({ url: `${MANAGEMENT}/rent-policy`, params: { shopId } })
export const saveHotelRentPolicies = (shopId: string | number | undefined, data: any[]) =>
  merchantApi.put({ url: `${MANAGEMENT}/rent-policy`, params: { shopId }, data })
export const getHotelMembers = (shopId?: string | number, keyword?: string) =>
  merchantApi.get({ url: `${MANAGEMENT}/member/page`, params: { shopId, keyword } })
export const issueHotelMemberCard = (shopId: string | number | undefined, data: any) =>
  merchantApi.post({ url: `${MANAGEMENT}/member/card`, params: { shopId }, data })
export const getHotelMemberLevels = (shopId?: string | number) =>
  merchantApi.get({ url: `${MANAGEMENT}/member/levels`, params: { shopId } })
export const saveHotelMemberLevels = (shopId: string | number | undefined, data: any[]) =>
  merchantApi.put({ url: `${MANAGEMENT}/member/levels`, params: { shopId }, data })
export const changeHotelMemberPoints = (shopId: string | number | undefined, data: any) =>
  merchantApi.post({ url: `${MANAGEMENT}/member/points`, params: { shopId }, data })
export const mergeHotelMembers = (shopId: string | number | undefined, data: any) =>
  merchantApi.post({ url: `${MANAGEMENT}/member/merge`, params: { shopId }, data })
export const getMerchantHotelCouponPage = (shopId: string | number | undefined, params: any) =>
  merchantApi.get({ url: `${MANAGEMENT}/coupon/page`, params: { ...params, shopId } })
export const createMerchantHotelCoupon = (shopId: string | number | undefined, data: any) =>
  merchantApi.post({ url: `${MANAGEMENT}/coupon`, params: { shopId }, data })
export const sendMerchantHotelCoupon = (shopId: string | number | undefined, data: any) =>
  merchantApi.post({ url: `${MANAGEMENT}/coupon/send`, params: { shopId }, data })
